import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Tag, Space, Alert, Button } from 'antd';
import {
  ExperimentOutlined, MedicineBoxOutlined, CalculatorOutlined, ApiOutlined,
  ThunderboltOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  CloseCircleOutlined, PlusCircleOutlined, UnorderedListOutlined,
  RightOutlined, HeartOutlined, SafetyCertificateOutlined,
  FileTextOutlined, WalletOutlined, CrownOutlined,
} from '@ant-design/icons';
import { healthAPI, drugAPI, notificationAPI, siteAPI, caseAPI, coinAPI, subscriptionAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import DrugActivation from '../components/DrugActivation';
import { useResponsive } from '../contexts/ResponsiveContext';

const { Title, Paragraph, Text } = Typography;

const DEFAULT_FEATURES = [
  {
    title: 'AI模型匹配',
    icon: <ExperimentOutlined />,
    color: '#0B2545',
    content: '输入患者基本信息(体重、纠正胎龄、肌酐等),AI自动推荐最适合的群体PK模型,包含匹配评分和推荐理由。',
  },
  {
    title: '初始给药计算',
    icon: <CalculatorOutlined />,
    color: '#C5A55A',
    content: '基于选定模型和目标AUC/Css/%fT>MIC,计算推荐的初始给药方案,包括剂量、间隔、预测浓度和安全检查。',
  },
  {
    title: 'TDM剂量优化',
    icon: <HeartOutlined />,
    color: '#0891B2',
    content: '结合TDM实测浓度，获取个体PK参数，优化后续给药方案，实现真正的个体化精准给药。',
  },
];

/* ── Stat Card ── */
const StatCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  onClick?: () => void;
}> = ({ icon, iconBg, label, value, sub, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: '#fff',
      borderRadius: 16,
      padding: '20px 24px',
      boxShadow: '0 1px 4px rgba(11,37,69,0.07), 0 4px 16px rgba(11,37,69,0.04)',
      border: '1px solid rgba(11,37,69,0.06)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s, transform 0.2s',
      display: 'flex', alignItems: 'center', gap: 16,
    }}
    onMouseEnter={e => { if (onClick) { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(11,37,69,0.12)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; } }}
    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(11,37,69,0.07), 0 4px 16px rgba(11,37,69,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'none'; }}
  >
    <div style={{
      width: 52, height: 52, borderRadius: 14, background: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 22, flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, color: '#6B7A8D', fontWeight: 500, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#0B2545', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

/* ── Feature Card ── */
const FeatureCard: React.FC<{ icon: React.ReactNode; iconColor: string; title: string; content: string; compact?: boolean }> = ({ icon, iconColor, title, content, compact }) => (
  <div style={{
    background: '#fff', borderRadius: compact ? 16 : 14, padding: compact ? '18px 16px 16px' : '20px 20px 18px',
    border: '1px solid rgba(11,37,69,0.07)',
    boxShadow: '0 1px 3px rgba(11,37,69,0.05)',
    height: '100%',
  }}>
    <div style={{
      width: compact ? 44 : 40, height: compact ? 44 : 40, borderRadius: 12,
      background: `${iconColor}18`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: compact ? 20 : 18, color: iconColor, marginBottom: compact ? 14 : 12,
    }}>
      {icon}
    </div>
    <div style={{ fontWeight: 700, fontSize: compact ? 14 : 15, color: '#0B2545', marginBottom: compact ? 6 : 8, lineHeight: 1.45 }}>{title}</div>
    <div style={{ fontSize: compact ? 12 : 13, color: '#6B7A8D', lineHeight: compact ? 1.7 : 1.65 }}>{content}</div>
  </div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { tokens } = useTheme();
  const { isMobileShell, isPhone } = useResponsive();
  const [health, setHealth] = useState<any>(null);
  const [healthError, setHealthError] = useState(false);
  const [caseCount, setCaseCount] = useState<number | null>(null);
  const [coinBalance, setCoinBalance] = useState<number | null>(null);
  const [planName, setPlanName] = useState<string>('');
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [showActivation, setShowActivation] = useState(false);

  useEffect(() => {
    healthAPI.check().then(setHealth).catch(() => setHealthError(true));
    caseAPI.stats().then((data) => setCaseCount(data.total_count)).catch(() => setCaseCount(0));
    coinAPI.getBalance().then((res: any) => setCoinBalance(res.balance ?? 0)).catch(() => setCoinBalance(0));
    subscriptionAPI.getCurrent().then((res: any) => setPlanName(res?.plan_display_name || '免费版')).catch(() => setPlanName('免费版'));
    notificationAPI.getAnnouncements().then(data => setAnnouncements(data.items || [])).catch(() => {});
    siteAPI.getConfig('feature_modules').then(data => {
      if (Array.isArray(data.value) && data.value.length > 0) setFeatures(data.value);
    }).catch(() => {});
    drugAPI.activationStatus().then(data => {
      if (data.needs_activation) { setNeedsActivation(true); setShowActivation(true); }
    }).catch(() => {});
  }, []);

  const latestAnnouncement = announcements.length > 0 ? announcements[0] : null;

  /* System status */
  const apiOk = !!health?.services?.api;
  const rOk = !!health?.services?.r_service;
  const checking = !health && !healthError;
  const allOk = apiOk && rOk;
  const partial = apiOk && !rOk && !!health;
  const statusColor = checking ? '#8c8c8c' : healthError ? '#f5222d' : allOk ? '#52c41a' : partial ? '#C5A55A' : '#f5222d';
  const statusLabel = checking ? '检测中…' : healthError ? '连接失败' : allOk ? '运行正常' : partial ? '部分异常' : '异常';
  const StatusIcon = checking ? ApiOutlined : healthError ? CloseCircleOutlined : allOk ? CheckCircleOutlined : ExclamationCircleOutlined;

  return (
    <div style={{ width: '100%', minWidth: 0, paddingBottom: isMobileShell ? 12 : 0 }}>

      {/* Page header */}
      <div style={{ marginBottom: isMobileShell ? 18 : 24 }}>
        <Title level={4} style={{ margin: 0, color: '#0B2545', fontWeight: 700 }}>工作台</Title>
        {/* <Text style={{ color: '#6B7A8D', fontSize: 13 }}>个体化精准给药决策支持平台 · MIPD</Text> */}
      </div>

      {/* Alerts */}
      {needsActivation && (
        <Alert message="请先激活药物" description="您尚未激活任何药物，请选择需要使用的药物以开始计算。"
          type="warning" showIcon icon={<ThunderboltOutlined />}
          action={<Button type="primary" size="small" onClick={() => setShowActivation(true)}>立即激活</Button>}
          style={{ marginBottom: 16, borderRadius: 10 }} />
      )}
      {latestAnnouncement && (
        <Alert message={latestAnnouncement.title} description={latestAnnouncement.content}
          type={latestAnnouncement.type || 'info'} showIcon closable
          style={{ marginBottom: 16, borderRadius: 10 }} />
      )}

      {/* KPI row */}
      <Row gutter={[12, 12]} style={{ marginBottom: isMobileShell ? 18 : 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<FileTextOutlined style={{ color: '#0B2545' }} />}
            iconBg="rgba(11,37,69,0.08)"
            label="我的病例"
            value={<>{caseCount === null ? '…' : caseCount} <span style={{ fontSize: 16, fontWeight: 500, color: '#6B7A8D' }}>例</span></>}
            sub={<Text style={{ fontSize: 12, color: '#0891B2', cursor: 'pointer' }} onClick={() => navigate('/history')}>查看历史 →</Text>}
            onClick={() => navigate('/history')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<WalletOutlined style={{ color: coinBalance !== null && coinBalance < 10 ? '#E6A817' : '#C5A55A' }} />}
            iconBg="rgba(197,165,90,0.12)"
            label="剩余金币"
            value={<>{coinBalance === null ? '…' : coinBalance} <span style={{ fontSize: 16, fontWeight: 500, color: '#6B7A8D' }}>枚</span></>}
            sub={
              coinBalance !== null && coinBalance < 10
                ? <Text style={{ fontSize: 12, color: '#E6A817', cursor: 'pointer' }} onClick={() => navigate('/personal-account')}>余额不足，去充值 →</Text>
                : <Text style={{ fontSize: 12, color: '#0891B2', cursor: 'pointer' }} onClick={() => navigate('/personal-account')}>查看明细 →</Text>
            }
            onClick={() => navigate('/personal-account')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CrownOutlined style={{ color: '#C5A55A' }} />}
            iconBg="rgba(197,165,90,0.12)"
            label="当前套餐"
            value={<span style={{ fontSize: 20 }}>{planName || '…'}</span>}
            sub={<Text style={{ fontSize: 12, color: '#0891B2', cursor: 'pointer' }} onClick={() => navigate('/subscription')}>套餐详情 →</Text>}
            onClick={() => navigate('/subscription')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<StatusIcon style={{ color: statusColor }} />}
            iconBg={`${statusColor}18`}
            label="系统状态"
            value={<span style={{ color: statusColor, fontSize: 20 }}>{statusLabel}</span>}
            sub={
              <Space size={4}>
                <Tag icon={apiOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={apiOk ? 'success' : 'error'} style={{ fontSize: 11, margin: 0 }}>API {apiOk ? '正常' : '异常'}</Tag>
                <Tag icon={rOk ? <CheckCircleOutlined /> : health ? <ExclamationCircleOutlined /> : <CloseCircleOutlined />} color={rOk ? 'success' : health ? 'warning' : 'error'} style={{ fontSize: 11, margin: 0 }}>R引擎 {rOk ? '正常' : health ? '异常' : '离线'}</Tag>
              </Space>
            }
          />
        </Col>
      </Row>

      {/* Quick actions */}
      <div style={{ marginBottom: isMobileShell ? 18 : 24 }}>
        <div style={{ fontSize: isPhone ? 12 : 13, fontWeight: 700, color: '#6B7A8D', letterSpacing: isPhone ? '0.02em' : '0.06em', marginBottom: 12 }}>快捷操作</div>
        <Row gutter={[12, 12]}>
          {[
            { icon: <PlusCircleOutlined />, label: '新建病例', desc: '开始新的给药计算', path: '/new-case', color: '#0B2545' },
            { icon: <UnorderedListOutlined />, label: '病例列表', desc: '查看所有病例记录', path: '/cases', color: '#0891B2' },
            { icon: <MedicineBoxOutlined />, label: '支持药物', desc: '查看药物与模型', path: '/drugs', color: '#C5A55A' },
            { icon: <SafetyCertificateOutlined />, label: '激活码管理', desc: '管理药物激活码', path: '/activation', color: '#52c41a' },
          ].map(item => (
            <Col xs={24} sm={12} lg={6} key={item.path}>
              <div
                onClick={() => navigate(item.path)}
                style={{
                  background: '#fff', borderRadius: 14, padding: isPhone ? '14px 14px' : '14px 16px',
                  border: '1px solid rgba(11,37,69,0.07)',
                  cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = item.color; (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 16px ${item.color}20`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(11,37,69,0.07)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
              >
                <div style={{ width: isPhone ? 38 : 36, height: isPhone ? 38 : 36, borderRadius: 10, background: `${item.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: item.color, flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: isPhone ? 16 : 14, color: '#0B2545', lineHeight: 1.3 }}>{item.label}</div>
                  <div style={{ fontSize: isPhone ? 13 : 12, color: '#8C9BAB', marginTop: 2, lineHeight: 1.45 }}>{item.desc}</div>
                </div>
                <RightOutlined style={{ fontSize: 11, color: '#C0C8D2', marginLeft: 'auto' }} />
              </div>
            </Col>
          ))}
        </Row>
      </div>

      {/* Core features */}
      <div>
        <div style={{ fontSize: isPhone ? 12 : 13, fontWeight: 700, color: '#6B7A8D', letterSpacing: isPhone ? '0.02em' : '0.06em', marginBottom: 12 }}>平台核心功能</div>
        <Row gutter={[12, 12]}>
          {features.map((f: any, i: number) => {
            const def = DEFAULT_FEATURES[i] || DEFAULT_FEATURES[0];
            return (
              <Col xs={24} sm={12} lg={8} key={i}>
                <FeatureCard
                  icon={(f as any).icon || def.icon}
                  iconColor={(f as any).color || def.color}
                  title={f.title}
                  content={f.content}
                  compact={isMobileShell}
                />
              </Col>
            );
          })}
        </Row>
      </div>

      <DrugActivation visible={showActivation} onClose={() => setShowActivation(false)} onActivated={() => setNeedsActivation(false)} />
    </div>
  );
};

export default Dashboard;
